import type { FeedEvent } from '@/lib/api/apiTypes';
import { formatEventDayLabel, formatEventTime, getRelativeTime } from '@/features/events/eventFormatting';
import type { FeedTimeWindow } from '@/features/events/feed/feedPreferences';

const POSTER_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const POSTER_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});
const COMPACT_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

type RelativeTime = ReturnType<typeof getRelativeTime>;

export function getTimeFilterRangeLabel(value: FeedTimeWindow) {
  switch (value) {
    case 'now':
      return 'next 2h';
    case 'tonight':
      return 'until 2am';
    case 'today':
      return 'until 2am';
    case 'tomorrow':
      return '2am-2am';
    case 'weekend':
      return 'Fri 5pm-Mon 2am';
    case 'week':
      return 'next 7d';
    default:
      return 'until 2am';
  }
}

export function getNearbyEmptyStateTitle(timeWindow: FeedTimeWindow) {
  switch (timeWindow) {
    case 'tonight':
      return 'Nothing happening nearby tonight';
    case 'today':
      return 'Nothing happening nearby today';
    case 'tomorrow':
      return 'Nothing lined up tomorrow';
    case 'weekend':
      return 'Looks quiet this weekend';
    default:
      return 'Nothing nearby right now';
  }
}

export function formatFeedPosterLabel(startsAt: string) {
  return `${formatEventDayLabel(startsAt)} • ${formatEventTime(startsAt)}`;
}

export function formatPosterDayLabel(startsAt: string) {
  const eventDate = new Date(startsAt);
  const now = new Date();
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (isSameDay(eventDate, now)) return 'TODAY';
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (isSameDay(eventDate, tomorrow)) return 'TOMORROW';
  return POSTER_DAY_FORMATTER.format(eventDate)
    .replace(',', '')
    .toUpperCase();
}

export function formatPosterTimeLabel(value: string) {
  return POSTER_TIME_FORMATTER.format(new Date(value))
    .replace(':00', '')
    .toUpperCase();
}

export function getFeedPrimaryTimeLabel(
  event: Pick<FeedEvent, 'doors_at' | 'starts_at'>,
  relTime: RelativeTime
) {
  if (relTime.label === 'Happening now') {
    return `Happening now • Started ${formatCompactTime(event.starts_at)}`;
  }

  if (!event.doors_at) {
    return relTime.label;
  }

  return `Doors ${formatCompactTime(event.doors_at)} • Starts ${formatCompactTime(event.starts_at)}`;
}

export function formatFeedSheetTimeRange(startsAt: string, endsAt?: string | null) {
  if (!endsAt) {
    return formatCompactTime(startsAt);
  }
  return `${formatCompactTime(startsAt)} - ${formatCompactTime(endsAt)}`;
}

export function formatCompactTime(value: string) {
  return COMPACT_TIME_FORMATTER.format(new Date(value))
    .replace(':00', '')
    .replace(' ', '')
    .toLowerCase();
}

export function formatDistanceLabel(distanceMeters: number) {
  const miles = distanceMeters / 1609.34;
  if (!miles || Number.isNaN(miles)) {
    return 'Nearby';
  }
  if (miles < 0.1) {
    return '<0.1 mi';
  }
  if (miles < 10) {
    return `${miles.toFixed(1)} mi`;
  }
  return `${Math.round(miles)} mi`;
}

export function formatWebFeedDistanceLabel(value?: number | null) {
  if (!value || value <= 0) return null;
  const miles = value / 1609.34;
  if (miles < 0.1) return '<0.1 mi away';
  if (miles < 10) return `${miles.toFixed(1)} mi away`;
  return `${Math.round(miles)} mi away`;
}

export function formatApproximateFeedDistanceLabel(
  distanceMeters: number,
  isApproximate: boolean
) {
  const label = formatDistanceLabel(distanceMeters);
  return isApproximate ? `~ ${label}` : label;
}

export function getRelativeTimeTextClassName(relTime: RelativeTime) {
  if (relTime.label === 'Happening now') {
    return 'text-status-live';
  }
  if (relTime.label.startsWith('Starting in')) {
    return 'text-status-soon';
  }
  if (relTime.label === 'Ended') {
    return 'text-status-neutral';
  }
  return 'text-status-neutral';
}

export function toFriendlyFeedError(error: string | null) {
  if (!error) {
    return {
      title: 'Something went wrong.',
      body: 'Please try again.',
    };
  }

  const normalized = error.toLowerCase();
  if (
    normalized.includes('unable to reach api at') ||
    normalized.includes('network request failed')
  ) {
    return {
      title: "Can't connect right now.",
      body: "We couldn't reach Radious. Check your connection and try again.",
    };
  }

  return {
    title: 'Something went wrong.',
    body: 'We could not load those events. Try again in a moment.',
  };
}
